import { create } from 'zustand';
import events from '../config/events';
import { RoomEvent } from 'livekit-client';
import CONSTANTS from '../config/constants';
import { sessionViewerEvents } from '../events/SessionViewerEventEmitter';

export const createEventStore = (initialState = {}) =>
  create((set, get) => ({
    stores: {},
    validEvents: events.validEvents,
    configuredEvents: {},
    generalEvents: {},
    participantEvents: {},
    roomEvents: {},
    registeredEvents: {},
    registeredRoomListeners: new Set(),
    sessionContext: {
      sessionSid: null,
      examSid: null,
      installSid: null,
    },

    injectDependentStores: (stores) => {
      set({ stores });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    initialize: async (props, config) => {
      // setup some default listeners that will trigger events...
      window.addEventListener('offline', () => {
        get().trigger('onOffline');
      });

      window.addEventListener('online', () => {
        get().trigger('onOnline');
      });

      const roomEvents = {};
      const participantEvents = {};
      const generalEvents = {};

      Object.keys(props).forEach((event) => {
        // now if the prop matches an event, call register to add it...
        if (get().validEvents[event]) {
          const eventConfig = get().validEvents[event];
          if (eventConfig.context === 'room') {
            roomEvents[event] = {
              config: eventConfig,
              function: props[event],
            };
          } else if (eventConfig.context === 'participant') {
            participantEvents[event] = {
              config: eventConfig,
              function: props[event],
            };
          } else {
            generalEvents[event] = {
              config: eventConfig,
              function: props[event],
            };
          }
        }
      });

      set({
        roomEvents,
        participantEvents,
        generalEvents,
        sessionContext: {
          sessionSid: config.sessionSid,
          examSid: config.examSid,
          installSid: config.installSid,
        }
      });

      // Call the function to setup all the room events...
      get().setupGeneralEvents();
      get().setupRoomEvents();

      const room = get().storeState('appStore').room;

      if (room) {
        // provision all events for any existing participants once the room is connected...
        room.on(RoomEvent.Connected, () => {
          try {
            get().setupParticipantEvents(room.localParticipant);
          } catch (e) {
            console.log(e);
          }

          // now loop all the remote participants and setup the events for them...
          room.remoteParticipants.forEach((participant) => {
            try {
              get().setupParticipantEvents(participant);
            } catch (e) {
              console.log(e);
            }
          });
        });

        // now whenever a new participant connects, lets register all participant events...
        room.on(RoomEvent.ParticipantConnected, (participant) => {
          get().setupParticipantEvents(participant);
        });
      }
    },

    setupGeneralEvents: () => {
      Object.keys(get().generalEvents).forEach((eventName) => {
        const event = get().generalEvents[eventName];
        get().register(eventName, event.function);
      });
    },

    setupRoomEvents: () => {
      const room = get().storeState('appStore').room;
      Object.keys(get().roomEvents).forEach((eventName) => {
        const event = get().roomEvents[eventName];
        const listenerKey = `${event.config.liveKitEvent}:${eventName}`;

        // Check if this specific event listener has already been registered
        if (get().registeredRoomListeners.has(listenerKey)) {
          console.log(`Event listener ${listenerKey} already registered, skipping registration.`);
          return;
        }

        // add the listener to the room...
        //console.log(`Registered Event: ${eventName} attached to room.`);
        if (room) {
          room.on(event.config.liveKitEvent, (...args) => {
            get().executeEvent(event, args);
          });

          // Track that we've registered this listener
          get().registeredRoomListeners.add(listenerKey);

        }
      });

      if (room) {
        if (!get().registeredRoomListeners.has('dataReceived')) {
          get().registeredRoomListeners.add('dataReceived');
          room.on('dataReceived', (payload, participant, kind) => {

            const dataMessage = get()
              .storeState('appStore')
              .mediaClient.processDataMessage(payload, participant, kind);

            if (dataMessage.event === CONSTANTS.EVENT_TYPES.ANNOTATIONS.ADDED) {
              // call to add this to the system...
              get()
                .storeState('examSessionStore')
                .loadAnnotation(dataMessage.data.payload);

              // now trigger that a new annotation was added as well..
              get().trigger('onFlaggingChange', { newFlag: dataMessage.data.payload });

              // Now if the annocation was the recording file is ready, we need to load the metadata...
              if (
                dataMessage.data.payload.typeId === 'recording-file-available'
              ) {
                get().storeState('examSessionStore').updateRecordingMetadata();
              }

            } else if (
              dataMessage.event === CONSTANTS.EVENT_TYPES.ANNOTATIONS.DELETED
            ) {
              get()
                .storeState('examSessionStore')
                .removeAnnotation(dataMessage.data.payload.sid);
            } else if (
              dataMessage.event === CONSTANTS.EVENT_TYPES.RECORDING.TRACK_CHANGED
            ) {
              //console.log(dataMessage);
            }
          });
        }
      }
    },

    setupParticipantEvents: (participant) => {
      Object.keys(get().participantEvents).forEach((eventName) => {
        const event = get().participantEvents[eventName];
        // add the listener to the participant...
        //console.log(`Registered Event: ${eventName} attached to participant (${participant.participantInfo.identity}).`, event);
        participant.on(event.config.liveKitEvent, (...args) => {
          get().executeEvent(event, args);
        });
      });
    },

    executeEvent: (event, args) => {
      try {
        const functionToRun = event.function;
        if (event.config.arguments) {
          const argMap = {};
          event.config.arguments.forEach((key, index) => {
            argMap[key] = args[index];
          });
          //Only run if the filter function returns true or there is no filter...
          if (
            event.config.filter &&
            typeof event.config.filter === 'function' &&
            event.config.filter(argMap)
          ) {
            functionToRun(argMap);
          }
        } else {
          // if there are no defined arguments, just call the function...
          functionToRun();
        }
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * Registers a callback function for a given event.
     *
     * Validates that the provided function is indeed a function and the event
     * is a valid event as per the validEvents list. If valid, the function
     * is registered to be called when the event is triggered.
     *
     * @param {string} event - The event name to register the callback for.
     * @param {Function} fn - The callback function to execute when the event is triggered.
     * @throws Will throw an error if the function is not a valid function or if the event is not found.
     */
    register: (event, fn) => {
      // ensure the function being passed in is a function
      if (typeof fn !== 'function') {
        throw Error('Function passed to register must be a function.');
      }
      // ensure the event is a valid event.
      if (!Object.keys(get().validEvents).includes(event)) {
        throw Error('Event not found.');
      }

      // register the event
      get().registeredEvents[event] = fn;
    },

    /**
     * Removes a registered event.
     *
     * This function deletes the event from the registeredEvents object,
     * effectively unregistering it so it will no longer be triggered.
     *
     * @param {string} event - The event name to remove from the registered events.
     */

    remove: async (event) => {
      // remove the event from registeredEvents
      delete get().registeredEvents[event];
    },

    /**
     * Cleanup all registered events.
     * 
     * This should be called when the SessionViewer is unmounted or disposed
     * to prevent memory leaks and duplicate event handlers.
     */
    cleanup: () => {
      // Clear all registered events
      set({ registeredEvents: {} });

      // Clear room listeners
      set({ registeredRoomListeners: new Set() });
    },

    /**
     * Trigger a registered event.
     *
     * This function triggers the provided event in two ways:
     * 1. Calls the registered callback prop (backward compatible)
     * 2. Emits to the event emitter (new pub/sub system)
     * 
     * This dual approach allows both traditional callback props and modern
     * event listeners to work simultaneously without conflicts.
     *
     * @param {string} event - The event name to trigger.
     * @param {Object} [options={}] - The options object to pass to the registered callback function.
     */
    trigger: async (event, options = {}) => {
      // Merge session context with provided options (options take precedence)
      const enrichedOptions = {
        ...get().sessionContext,
        ...options,
      };

      // 1. Call the registered callback prop (backward compatible)
      if (get().registeredEvents[event]) {
        get().registeredEvents[event](enrichedOptions);
      }

      // 2. Emit to the event emitter (new pub/sub system)
      // This allows multiple listeners without interfering with callback props
      sessionViewerEvents.emit(event, enrichedOptions);
    },
  }));
